import {useEffect, useState} from 'react'
import type {StackData} from '../page-data/payloads/header'
import {copyRichText} from '../helpers/copy-rich-text'
import {buildPullRequestLink} from '../helpers/build-pull-request-link'

const copyStatusMsDelay = 2000

export type CopyStackLinksStatus = 'success' | 'error'

type StackPullLink = Pick<StackData['pulls'][number], 'number' | 'title' | 'url'>
type Repository = {owner: string; repository: string}
type StackLink = Pick<StackData, 'number'> & {url: string}
type ClipboardContent = {html: string; plainText: string}
type CopyTarget = 'pullRequestLinks' | 'stackLink'

function toAbsoluteHttpUrl(path: string): string {
  const url = new URL(path, window.location.origin)
  if (url.protocol !== 'https:' && url.protocol !== 'http:') {
    throw new Error(`Unsupported URL protocol: ${url.protocol}`)
  }

  return url.toString()
}

function buildPullRequestLinksContent(pulls: StackPullLink[], repository: Repository): ClipboardContent {
  const htmlContainer = document.createElement('div')
  const urls = pulls.toReversed().map(pull => {
    const url = toAbsoluteHttpUrl(pull.url)
    const row = document.createElement('div')
    row.append(buildPullRequestLink({...pull, ...repository, url}))
    htmlContainer.append(row)
    return url
  })

  return {
    html: htmlContainer.innerHTML,
    plainText: urls.join('\n'),
  }
}

function buildStackLinkContent({number, url}: StackLink): ClipboardContent {
  const href = toAbsoluteHttpUrl(url)
  const htmlContainer = document.createElement('div')
  const anchor = document.createElement('a')
  anchor.href = href
  anchor.textContent = `Stack #${number}`
  htmlContainer.append(anchor)

  return {
    html: htmlContainer.innerHTML,
    plainText: href,
  }
}

export function useCopyStackLinks() {
  // The clipboard only holds the latest copy, so only the latest copy action shows feedback.
  const [copyState, setCopyState] = useState<{target: CopyTarget; status: CopyStackLinksStatus}>()

  useEffect(() => {
    if (!copyState) return

    const timeoutId = setTimeout(() => setCopyState(undefined), copyStatusMsDelay)
    return () => clearTimeout(timeoutId)
  }, [copyState])

  const copy = async (target: CopyTarget, buildContent: () => ClipboardContent) => {
    setCopyState(undefined)

    try {
      await copyRichText(buildContent())
      setCopyState({target, status: 'success'})
    } catch {
      setCopyState({target, status: 'error'})
    }
  }

  return {
    pullRequestLinksCopyStatus: copyState?.target === 'pullRequestLinks' ? copyState.status : undefined,
    stackLinkCopyStatus: copyState?.target === 'stackLink' ? copyState.status : undefined,
    copyPullRequestLinks: (pulls: StackPullLink[], repository: Repository) =>
      copy('pullRequestLinks', () => buildPullRequestLinksContent(pulls, repository)),
    copyStackLink: (stack: StackLink) => copy('stackLink', () => buildStackLinkContent(stack)),
    resetCopyStatus: () => setCopyState(undefined),
  }
}
